import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import SEO from './SEO';
import Transition from './Transition';
import SplitText from './SplitText';
import useReveal from '../hooks/useReveal';

const splitTextFrom = { opacity: 0, y: 60 };
const splitTextTo = { opacity: 1, y: 0 };

const LOGO_SRC = '/sk-logo.png';

/* ------------------------------------------------------------------ */
/* Data — mirrors tailwind.config.js                                   */
/* ------------------------------------------------------------------ */

const CHAPTERS = [
  { num: '01', id: 'introduction', label: 'Brand introduction', sub: 'Vision, mission & essence' },
  { num: '02', id: 'logo', label: 'Logo system', sub: 'The SK mark & clear space' },
  { num: '03', id: 'logo-usage', label: 'Logo usage', sub: 'Backgrounds & misuse' },
  { num: '04', id: 'colours', label: 'Colour palette', sub: 'Primaries, accents & tints' },
  { num: '05', id: 'typography', label: 'Typography', sub: 'Type family & hierarchy' },
  { num: '06', id: 'applications', label: 'Elements & applications', sub: 'Accents in the wild' },
];

const COLOR_GROUPS = [
  {
    id: 'base',
    label: 'Base & surfaces',
    colors: [
      { name: 'Ink', token: 'mainkleur / panel', hex: '#262624', rgb: '38 · 38 · 36', tone: 'light', usage: 'Text and dark sections' },
      { name: 'Cream', token: 'creme / surface', hex: '#f2f3d9', rgb: '242 · 243 · 217', tone: 'dark', usage: 'Page base (light sections)' },
      { name: 'Warm light', token: 'licht', hex: '#e4dbd2', rgb: '228 · 219 · 210', tone: 'dark', usage: 'Text on dark, buttons' },
      { name: 'Card', token: 'card', hex: '#ffffff', rgb: '255 · 255 · 255', tone: 'dark', usage: 'White card on cream' },
    ],
  },
  {
    id: 'action',
    label: 'Primary action — orange gradient',
    colors: [
      { name: 'Orange start', token: 'verloop-oranje-start', hex: '#9c4e17', rgb: '156 · 78 · 23', tone: 'light', usage: 'Start of the CTA gradient' },
      { name: 'Orange mid', token: 'verloop-oranje-mid', hex: '#f97b22', rgb: '249 · 123 · 34', tone: 'dark', usage: 'Main orange: links, accents' },
      { name: 'Orange end', token: 'verloop-oranje-end', hex: '#fe9043', rgb: '254 · 144 · 67', tone: 'dark', usage: 'End of gradient / lighter orange' },
    ],
  },
  {
    id: 'pastel',
    label: 'Pastel accents — per-section signature',
    colors: [
      { name: 'Periwinkle', token: 'periwinkle', hex: '#b1c1ef', rgb: '177 · 193 · 239', tone: 'dark', usage: 'Soft-blue accent' },
      { name: 'Coral', token: 'coral', hex: '#f2acac', rgb: '242 · 172 · 172', tone: 'dark', usage: 'Pink / coral accent' },
      { name: 'Butter', token: 'butter', hex: '#ffdd94', rgb: '255 · 221 · 148', tone: 'dark', usage: 'Warm yellow accent' },
    ],
  },
  {
    id: 'editorial',
    label: 'Secondary editorial accents — sparingly',
    colors: [
      { name: 'Olive', token: 'olive', hex: '#8ea78f', rgb: '142 · 167 · 143', tone: 'dark', usage: 'Muted green' },
      { name: 'Dusty', token: 'dusty', hex: '#9bb0c9', rgb: '155 · 176 · 201', tone: 'dark', usage: 'Muted blue' },
      { name: 'Clay', token: 'clay', hex: '#cf8b6c', rgb: '207 · 139 · 108', tone: 'dark', usage: 'Muted terracotta' },
    ],
  },
];

const TINT_ROWS = [
  { name: 'Ink', hex: '#262624' },
  { name: 'Orange', hex: '#f97b22' },
  { name: 'Periwinkle', hex: '#b1c1ef' },
  { name: 'Coral', hex: '#f2acac' },
];
const TINT_STEPS = [100, 80, 60, 40, 20];

const SEMANTIC_TOKENS = [
  { token: 'hairline', value: 'rgba(38,38,36,0.14)', usage: 'Subtle divider on light' },
  { token: 'hairline-light', value: 'rgba(228,219,210,0.18)', usage: 'Divider on dark' },
  { token: 'muted', value: 'rgba(38,38,36,0.6)', usage: 'Muted body text on light' },
];

const FONTS = [
  { name: 'Anton', className: 'font-anton', weights: '400', usage: 'Large display titles (hero, section headings)', specimenClass: 'font-anton uppercase tracking-[-2px]' },
  { name: 'Anton SC', className: 'font-anton-sc', weights: '400', usage: 'Small-caps variant (e.g. testimonial quote mark)', specimenClass: 'font-anton-sc tracking-[-1px]' },
  { name: 'Bricolage Grotesque', className: 'font-bricolage', weights: '200 – 800 (variable)', usage: 'Subheadings, buttons, labels', specimenClass: 'font-bricolage font-extrabold tracking-tight' },
  { name: 'Space Grotesk', className: 'font-space', weights: '300 – 700', usage: 'Body text and UI (global base font)', specimenClass: 'font-space font-medium' },
];

const TYPE_SCALE = [
  { role: 'Display', spec: 'Anton · 96–170px · −5% tracking', sample: 'Aa', cls: 'font-anton uppercase text-5xl sm:text-6xl tracking-[-3px]' },
  { role: 'Heading', spec: 'Bricolage Extrabold · 32–72px', sample: 'Section title', cls: 'font-bricolage font-extrabold text-3xl sm:text-4xl tracking-tight' },
  { role: 'Subhead', spec: 'Bricolage Semibold · 20–28px', sample: 'A supporting line', cls: 'font-bricolage font-semibold text-xl sm:text-2xl' },
  { role: 'Body', spec: 'Space Grotesk · 16–22px · 1.4 lh', sample: 'Body copy carries the detail and rhythm of every page.', cls: 'font-space text-base sm:text-lg' },
  { role: 'Eyebrow', spec: 'Space Grotesk · 12px · 0.22em caps', sample: 'EYEBROW LABEL', cls: 'font-space text-xs uppercase tracking-[0.22em]' },
];

const PRINCIPLES = [
  { k: 'Warmth', d: 'A cream and warm-beige base, never clinical white. The work feels human and inviting.' },
  { k: 'Craft', d: 'Editorial typography, hairline dividers and considered rhythm — detail over decoration.' },
  { k: 'Clarity', d: 'One orange accent guides attention. Calm in layout, clarity in action.' },
];

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const RunningHeader = ({ num, label, total = '06', dark }) => (
  <div
    className={`flex items-center justify-between pb-5 mb-10 sm:mb-12 border-b ${
      dark ? 'border-hairline-light' : 'border-hairline'
    }`}
  >
    <span className={`eyebrow ${dark ? 'text-licht/70' : 'text-muted'}`}>SK Design — Brand book</span>
    <span className={`font-space text-xs uppercase tracking-[0.22em] tabular-nums ${dark ? 'text-licht/70' : 'text-muted'}`}>
      {num} / {total}
    </span>
  </div>
);

const ColorSwatch = ({ color }) => {
  const labelColor = color.tone === 'light' ? 'text-licht/90' : 'text-mainkleur/80';
  return (
    <div className="reveal-up rounded-2xl border border-hairline bg-card overflow-hidden">
      <div className="h-28 sm:h-32 flex items-end justify-end p-3" style={{ backgroundColor: color.hex }}>
        <span className={`font-space text-[11px] sm:text-xs uppercase tracking-[0.16em] tabular-nums ${labelColor}`}>
          {color.hex}
        </span>
      </div>
      <div className="p-4 flex flex-col gap-1">
        <span className="font-bricolage font-extrabold text-mainkleur text-lg leading-tight">{color.name}</span>
        <code className="font-space text-[12px] text-[#f97b22]">{color.token}</code>
        <span className="font-space text-mainkleur/70 text-[12px] tabular-nums">RGB {color.rgb}</span>
        <span className="font-space text-muted text-[13px] leading-snug mt-1">{color.usage}</span>
      </div>
    </div>
  );
};

const LogoOnBg = ({ bg, invert, border, label }) => (
  <div className="reveal-up flex flex-col gap-2">
    <div
      className={`aspect-[4/3] rounded-2xl flex items-center justify-center ${border ? 'border border-hairline' : ''}`}
      style={{ background: bg }}
    >
      <img src={LOGO_SRC} alt="SK logo" className={`w-[42%] max-w-[120px] h-auto ${invert ? 'invert' : ''}`} />
    </div>
    <span className="font-space text-muted text-[12px] uppercase tracking-[0.16em]">{label}</span>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const BrandGuidelinesPage = () => {
  const location = useLocation();
  const revealRef = useReveal();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  return (
    <>
      <SEO
        title="Brand Guidelines"
        description="The SK Design brand book: logo system, colour palette, typography and usage guidelines for the visual identity."
        path="/brand-guidelines"
      />
      <Navbar />

      <main ref={revealRef} className="bg-creme">
        {/* ============================================================ */}
        {/* COVER                                                        */}
        {/* ============================================================ */}
        <section className="grain-overlay bg-mainkleur text-licht min-h-screen flex flex-col">
          <div className="max-w-[1422px] w-full mx-auto px-5 sm:px-6 lg:px-8 pt-28 sm:pt-32 lg:pt-36 flex items-center justify-between">
            <span className="eyebrow text-licht/70">SK Design</span>
            <span className="font-space text-xs uppercase tracking-[0.22em] text-licht/70">Visual identity · v1.0</span>
          </div>

          <div className="flex-1 max-w-[1422px] w-full mx-auto px-5 sm:px-6 lg:px-8 flex flex-col justify-center py-16">
            <img src={LOGO_SRC} alt="SK logo" className="invert w-24 sm:w-28 lg:w-32 h-auto mb-10 sm:mb-12" />
            <h1 className="font-anton uppercase text-licht text-[16vw] sm:text-[120px] lg:text-[180px] leading-[0.84] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-10px]">
              <SplitText
                text="BRAND"
                splitType="chars"
                delay={60}
                duration={1.2}
                ease="power3.out"
                from={splitTextFrom}
                to={splitTextTo}
                threshold={0}
                rootMargin={'+50px'}
              />
              <span className="inline-block w-full text-[#f97b22]">
                <SplitText
                  text="GUIDELINES"
                  splitType="chars"
                  delay={60}
                  duration={1.2}
                  ease="power3.out"
                  from={splitTextFrom}
                  to={splitTextTo}
                  threshold={0}
                  rootMargin={'+50px'}
                />
              </span>
            </h1>
            <p className="font-space text-licht/70 text-lg sm:text-xl max-w-[560px] mt-8 sm:mt-10 leading-[1.45]">
              The complete brand system behind the portfolio: logo, colour, typography and how it all stays consistent across every touchpoint.
            </p>
          </div>

          <div className="max-w-[1422px] w-full mx-auto px-5 sm:px-6 lg:px-8 pb-10 sm:pb-12 flex items-center justify-between border-t border-hairline-light pt-6">
            <span className="font-space text-xs uppercase tracking-[0.22em] text-licht/50">Sander Kuyken</span>
            <span className="font-space text-xs uppercase tracking-[0.22em] text-licht/50 animate-pulse">Scroll ↓</span>
          </div>
        </section>

        {/* ============================================================ */}
        {/* TABLE OF CONTENTS                                            */}
        {/* ============================================================ */}
        <section className="bg-creme text-mainkleur">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="00" label="Contents" />
            <h2 className="reveal-up font-anton uppercase text-mainkleur text-[12vw] sm:text-[64px] lg:text-[88px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px] mb-12 sm:mb-16">
              Contents
            </h2>
            <ol className="flex flex-col">
              {CHAPTERS.map((c) => (
                <li key={c.id} className="reveal-up">
                  <a
                    href={`#${c.id}`}
                    className="group flex items-baseline gap-5 sm:gap-8 py-5 sm:py-6 border-t border-hairline hover:border-mainkleur transition-colors duration-300"
                  >
                    <span className="section-number text-[#f97b22] text-2xl sm:text-3xl w-12 sm:w-16 flex-shrink-0 tabular-nums">
                      {c.num}
                    </span>
                    <span className="font-bricolage font-extrabold text-mainkleur text-2xl sm:text-4xl lg:text-5xl tracking-tight group-hover:translate-x-2 transition-transform duration-300">
                      {c.label}
                    </span>
                    <span className="ml-auto font-space text-muted text-sm sm:text-base hidden sm:block self-center">
                      {c.sub}
                    </span>
                  </a>
                </li>
              ))}
              <li className="border-t border-hairline" />
            </ol>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 01 — BRAND INTRODUCTION                                      */}
        {/* ============================================================ */}
        <section id="introduction" className="scroll-mt-24 grain-overlay bg-mainkleur text-licht">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="01" label="Brand introduction" dark />
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-5">
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">01</span>
                <h2 className="reveal-up font-anton uppercase text-licht text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Brand introduction
                </h2>
              </div>
              <div className="lg:col-span-7 flex flex-col gap-10">
                <p className="reveal-up font-space text-licht/80 text-xl sm:text-2xl leading-[1.4]">
                  SK Design is an independent design studio for branding, web and motion. The identity is warm and editorial: a cream canvas, ink-black typography and one purposeful orange accent.
                </p>
                <div className="reveal-up grid sm:grid-cols-2 gap-x-12 gap-y-8">
                  <div>
                    <span className="eyebrow text-[#f97b22]">Vision</span>
                    <p className="font-space text-licht/75 text-base sm:text-lg leading-[1.5] mt-3">
                      Design that is felt in every detail, not just seen — so even small brands look considered, human and built to last beyond trends.
                    </p>
                  </div>
                  <div>
                    <span className="eyebrow text-[#f97b22]">Mission</span>
                    <p className="font-space text-licht/75 text-base sm:text-lg leading-[1.5] mt-3">
                      Give brands a consistent visual voice across web, print and motion — from logo and colour to the finished experience.
                    </p>
                  </div>
                </div>

                <div className="reveal-up grid sm:grid-cols-3 gap-4 pt-2">
                  {PRINCIPLES.map((p) => (
                    <div key={p.k} className="rounded-2xl border border-hairline-light p-5 bg-white/[0.03]">
                      <span className="font-bricolage font-extrabold text-[#f97b22] text-lg inline-block mb-2">{p.k}</span>
                      <p className="font-space text-licht/70 text-sm leading-[1.5]">{p.d}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <blockquote className="reveal-up mt-14 sm:mt-20 border-t border-hairline-light pt-10">
              <p className="font-anton-sc text-licht text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-1px] max-w-[1100px]">
                “Good branding isn’t seen — it’s felt in every detail.”
              </p>
            </blockquote>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 02 — LOGO SYSTEM                                             */}
        {/* ============================================================ */}
        <section id="logo" className="scroll-mt-24 bg-creme text-mainkleur">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="02" label="Logo system" />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">02</span>
                <h2 className="reveal-up font-anton uppercase text-mainkleur text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Logo system
                </h2>
              </div>
              <p className="reveal-up font-space text-muted text-base sm:text-lg max-w-[420px]">
                The SK monogram is the core of the identity. Always use the original files — never redraw or re-proportion it.
              </p>
            </div>

            {/* Primary mark */}
            <div className="reveal-up rounded-3xl border border-hairline bg-card overflow-hidden grid md:grid-cols-2">
              <div className="aspect-[4/3] md:aspect-auto flex items-center justify-center p-12 bg-creme">
                <img src={LOGO_SRC} alt="SK primary logo" className="w-[55%] max-w-[260px] h-auto" />
              </div>
              <div className="p-8 sm:p-10 flex flex-col justify-center gap-4 border-t md:border-t-0 md:border-l border-hairline">
                <span className="eyebrow text-muted">Primary mark</span>
                <h3 className="font-bricolage font-extrabold text-mainkleur text-2xl sm:text-3xl">The SK monogram</h3>
                <p className="font-space text-muted text-base leading-[1.55]">
                  The S and K flow together into a single shape. This is the default rendering on light backgrounds. On dark surfaces, the reversed (cream/white) variant is used.
                </p>
              </div>
            </div>

            {/* Clear space & min size */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="reveal-up rounded-3xl border border-hairline bg-card p-8 sm:p-10">
                <span className="eyebrow text-muted">Clear space</span>
                <div className="mt-6 relative inline-flex p-8 rounded-xl border border-dashed border-[#f97b22]/50">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-card px-2 font-space text-[11px] text-[#f97b22] uppercase tracking-[0.18em]">x</span>
                  <img src={LOGO_SRC} alt="SK clear space" className="w-24 h-auto" />
                </div>
                <p className="font-space text-muted text-sm leading-[1.55] mt-6">
                  Keep at least the height of the letter “x” (≈ half the logo height) clear around the logo. No text or elements within that zone.
                </p>
              </div>
              <div className="reveal-up rounded-3xl border border-hairline bg-card p-8 sm:p-10">
                <span className="eyebrow text-muted">Minimum size</span>
                <div className="mt-6 flex items-end gap-8">
                  <div className="flex flex-col items-center gap-2">
                    <img src={LOGO_SRC} alt="SK min digital" className="w-16 h-auto" />
                    <span className="font-space text-[11px] text-muted uppercase tracking-[0.16em]">Digital · 32px</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <img src={LOGO_SRC} alt="SK min print" className="w-10 h-auto" />
                    <span className="font-space text-[11px] text-muted uppercase tracking-[0.16em]">Print · 10mm</span>
                  </div>
                </div>
                <p className="font-space text-muted text-sm leading-[1.55] mt-6">
                  Below these sizes the monogram loses legibility. Use extra contrast at very small sizes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 03 — LOGO USAGE                                              */}
        {/* ============================================================ */}
        <section id="logo-usage" className="scroll-mt-24 bg-surface text-mainkleur border-t border-hairline">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="03" label="Logo usage" />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">03</span>
                <h2 className="reveal-up font-anton uppercase text-mainkleur text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Logo usage
                </h2>
              </div>
              <p className="reveal-up font-space text-muted text-base sm:text-lg max-w-[420px]">
                The logo must always stand free with sufficient contrast. Below are the approved background combinations.
              </p>
            </div>

            {/* Colour combinations */}
            <span className="eyebrow text-muted mb-5">Approved backgrounds</span>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              <LogoOnBg bg="#f2f3d9" label="Cream · positive" border />
              <LogoOnBg bg="#262624" invert label="Ink · reversed" />
              <LogoOnBg bg="linear-gradient(135deg,#9c4e17,#fe9043)" invert label="Orange · reversed" />
              <LogoOnBg bg="#b1c1ef" label="Periwinkle · positive" />
            </div>

            {/* Do / Don't */}
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="reveal-up rounded-3xl border border-hairline bg-card p-8">
                <span className="font-bricolage font-extrabold text-[#2f7d52] text-lg flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2f7d52] text-white text-sm">✓</span>
                  Do
                </span>
                <ul className="flex flex-col gap-3 font-space text-mainkleur/80 text-sm sm:text-base">
                  <li className="flex gap-3"><span className="text-[#2f7d52]">—</span> Use the original files, unaltered.</li>
                  <li className="flex gap-3"><span className="text-[#2f7d52]">—</span> Respect the clear space around the logo.</li>
                  <li className="flex gap-3"><span className="text-[#2f7d52]">—</span> Choose the variant with the highest contrast.</li>
                  <li className="flex gap-3"><span className="text-[#2f7d52]">—</span> Place it on calm, even backgrounds.</li>
                </ul>
              </div>
              <div className="reveal-up rounded-3xl border border-hairline bg-card p-8">
                <span className="font-bricolage font-extrabold text-[#b23b3b] text-lg flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#b23b3b] text-white text-sm">✕</span>
                  Don't
                </span>
                <ul className="flex flex-col gap-3 font-space text-mainkleur/80 text-sm sm:text-base">
                  <li className="flex gap-3"><span className="text-[#b23b3b]">—</span> Don’t distort, rotate or recolour it.</li>
                  <li className="flex gap-3"><span className="text-[#b23b3b]">—</span> Don’t add shadows, outlines or effects.</li>
                  <li className="flex gap-3"><span className="text-[#b23b3b]">—</span> Don’t place it on busy photos without an overlay.</li>
                  <li className="flex gap-3"><span className="text-[#b23b3b]">—</span> Don’t stretch it or change its proportions.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 04 — COLOUR PALETTE                                          */}
        {/* ============================================================ */}
        <section id="colours" className="scroll-mt-24 bg-creme text-mainkleur">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="04" label="Colour palette" />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">04</span>
                <h2 className="reveal-up font-anton uppercase text-mainkleur text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Colour palette
                </h2>
              </div>
              <p className="reveal-up font-space text-muted text-base sm:text-lg max-w-[420px]">
                Tokens map 1-to-1 with <code className="text-[#f97b22]">tailwind.config.js</code>. Orange is the only primary action accent.
              </p>
            </div>

            {/* Gradient */}
            <div className="reveal-up mb-12 sm:mb-14">
              <span className="font-space text-muted text-xs uppercase tracking-[0.22em]">Primary CTA gradient</span>
              <div
                className="mt-3 h-16 sm:h-20 rounded-2xl border border-hairline"
                style={{ background: 'linear-gradient(to left, #9c4e17 35%, #f97b22, #fe9043)' }}
              />
            </div>

            <div className="flex flex-col gap-12 sm:gap-14">
              {COLOR_GROUPS.map((group) => (
                <div key={group.id}>
                  <h3 className="font-space text-muted text-xs sm:text-sm uppercase tracking-[0.22em] mb-5">{group.label}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                    {group.colors.map((color) => (
                      <ColorSwatch key={color.token} color={color} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Tints */}
              <div className="reveal-up">
                <h3 className="font-space text-muted text-xs sm:text-sm uppercase tracking-[0.22em] mb-5">Using tints</h3>
                <p className="font-space text-muted text-sm max-w-[640px] mb-6">
                  Lighter tints are made by placing the base colour on white at 80/60/40/20%. Use tints for backgrounds, hovers and layering — never as a replacement for the primary orange.
                </p>
                <div className="rounded-2xl border border-hairline bg-card overflow-hidden divide-y divide-hairline">
                  {TINT_ROWS.map((row) => (
                    <div key={row.name} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 sm:px-6">
                      <span className="font-bricolage font-extrabold text-mainkleur text-sm sm:w-28 flex-shrink-0">{row.name}</span>
                      <div className="grid grid-cols-5 gap-2 flex-1">
                        {TINT_STEPS.map((step) => (
                          <div key={step} className="flex flex-col items-center gap-1.5">
                            <div className="w-full h-12 sm:h-14 rounded-lg bg-white overflow-hidden border border-hairline">
                              <div className="w-full h-full" style={{ backgroundColor: row.hex, opacity: step / 100 }} />
                            </div>
                            <span className="font-space text-[11px] text-muted tabular-nums">{step}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Semantic */}
              <div className="reveal-up">
                <h3 className="font-space text-muted text-xs sm:text-sm uppercase tracking-[0.22em] mb-5">Semantic helpers (rgba)</h3>
                <div className="rounded-2xl border border-hairline bg-card divide-y divide-hairline">
                  {SEMANTIC_TOKENS.map((t) => (
                    <div key={t.token} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 p-4 sm:px-6">
                      <code className="font-space text-[13px] text-[#f97b22] sm:w-44 flex-shrink-0">{t.token}</code>
                      <span className="font-space text-mainkleur text-[13px] tabular-nums sm:w-60 flex-shrink-0">{t.value}</span>
                      <span className="font-space text-muted text-[13px]">{t.usage}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 05 — TYPOGRAPHY                                              */}
        {/* ============================================================ */}
        <section id="typography" className="scroll-mt-24 grain-overlay bg-mainkleur text-licht">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="05" label="Typography" dark />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">05</span>
                <h2 className="reveal-up font-anton uppercase text-licht text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Typography
                </h2>
              </div>
              <p className="reveal-up font-space text-licht/70 text-base sm:text-lg max-w-[420px]">
                Four families with a clear role. Anton carries the display, Bricolage the structure, Space Grotesk the text.
              </p>
            </div>

            <div className="flex flex-col gap-6 sm:gap-7">
              {FONTS.map((font) => (
                <div key={font.name} className="reveal-up rounded-2xl border border-hairline-light bg-white/[0.03] p-6 sm:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
                    <div className={`${font.specimenClass} text-licht text-[64px] sm:text-[88px] lg:text-[110px] leading-[0.85] flex-shrink-0`}>
                      Aa
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                        <span className="font-bricolage font-extrabold text-licht text-2xl sm:text-3xl tracking-tight">{font.name}</span>
                        <code className="font-space text-[13px] text-[#f97b22]">{font.className}</code>
                        <span className="font-space text-licht/60 text-[13px] uppercase tracking-[0.16em]">{font.weights}</span>
                      </div>
                      <p className={`${font.specimenClass} text-licht text-xl sm:text-2xl leading-[1.15] mb-2`}>
                        The quick brown fox jumps over the lazy dog
                      </p>
                      <p className={`${font.specimenClass} text-licht/55 text-base sm:text-lg leading-[1.2]`}>
                        ABCDEFGHIJKLM nopqrstuvwxyz 0123456789
                      </p>
                      <span className="font-space text-licht/60 text-[13px] leading-snug inline-block mt-3">{font.usage}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Hierarchy scale */}
            <div className="reveal-up mt-12">
              <h3 className="font-space text-licht/60 text-xs sm:text-sm uppercase tracking-[0.22em] mb-5">Type hierarchy</h3>
              <div className="rounded-2xl border border-hairline-light divide-y divide-[rgba(228,219,210,0.12)]">
                {TYPE_SCALE.map((t) => (
                  <div key={t.role} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8 p-5 sm:px-6">
                    <span className="font-space text-licht/55 text-[12px] uppercase tracking-[0.18em] sm:w-24 flex-shrink-0">{t.role}</span>
                    <span className={`${t.cls} text-licht flex-1 min-w-0 truncate`}>{t.sample}</span>
                    <span className="font-space text-licht/45 text-[12px] sm:w-64 flex-shrink-0 sm:text-right">{t.spec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 06 — ELEMENTS & APPLICATIONS                                 */}
        {/* ============================================================ */}
        <section id="applications" className="scroll-mt-24 bg-creme text-mainkleur">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
            <RunningHeader num="06" label="Elements & applications" />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <span className="section-number text-[#f97b22] text-5xl sm:text-6xl inline-block mb-6">06</span>
                <h2 className="reveal-up font-anton uppercase text-mainkleur text-[12vw] sm:text-[56px] lg:text-[80px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                  Elements & applications
                </h2>
              </div>
              <p className="reveal-up font-space text-muted text-base sm:text-lg max-w-[420px]">
                A few examples of how the elements come together in real applications.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {/* Business card mock */}
              <div className="reveal-up grain-overlay bg-mainkleur text-licht rounded-3xl p-8 aspect-[3/4] flex flex-col justify-between">
                <img src={LOGO_SRC} alt="SK" className="invert w-12 h-auto" />
                <div>
                  <p className="font-bricolage font-extrabold text-licht text-xl">Sander Kuyken</p>
                  <p className="font-space text-licht/60 text-sm mt-1">Brand · Web · Motion</p>
                  <div className="w-10 h-1 bg-[#f97b22] rounded-full mt-4" />
                </div>
              </div>

              {/* CTA button + tag specimen */}
              <div className="reveal-up bg-card border border-hairline rounded-3xl p-8 aspect-[3/4] flex flex-col justify-center gap-5">
                <span className="eyebrow text-muted">UI elements</span>
                <button className="font-bricolage font-bold text-licht text-base px-6 py-3 rounded-full" style={{ background: 'linear-gradient(135deg,#9c4e17,#fe9043)' }}>
                  Start a project →
                </button>
                <div className="flex flex-wrap gap-2">
                  <span className="font-space text-xs uppercase tracking-[0.12em] text-mainkleur border border-mainkleur/20 rounded-full px-3 py-1.5">Branding</span>
                  <span className="font-space text-xs uppercase tracking-[0.12em] text-mainkleur border border-mainkleur/20 rounded-full px-3 py-1.5">Web</span>
                  <span className="font-space text-xs uppercase tracking-[0.12em] text-licht bg-mainkleur rounded-full px-3 py-1.5">Motion</span>
                </div>
                <div className="w-full h-px bg-hairline" />
                <p className="font-space text-muted text-sm leading-[1.5]">Hairlines, pill tags and the orange CTA form the basis of the UI.</p>
              </div>

              {/* Accent swatch stack / social */}
              <div className="reveal-up rounded-3xl overflow-hidden border border-hairline aspect-[3/4] flex flex-col">
                <div className="flex-1 grid grid-cols-3">
                  {['#b1c1ef', '#f2acac', '#ffdd94', '#8ea78f', '#9bb0c9', '#cf8b6c'].map((c) => (
                    <div key={c} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="bg-card p-6">
                  <span className="font-bricolage font-extrabold text-mainkleur text-lg inline-block">Accent system</span>
                  <p className="font-space text-muted text-sm mt-1 leading-[1.5]">Pastels give each section its own signature, always beneath the orange.</p>
                </div>
              </div>
            </div>

            {/* Closing */}
            <div className="reveal-up mt-16 sm:mt-20 border-t border-hairline pt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <img src={LOGO_SRC} alt="SK" className="w-12 h-auto" />
                <div>
                  <p className="font-bricolage font-extrabold text-mainkleur text-lg leading-tight">SK Design — Brand book</p>
                  <p className="font-space text-muted text-sm">Visual identity · v1.0 · {new Date().getFullYear()}</p>
                </div>
              </div>
              <a
                href="/contact"
                className="inline-flex items-center justify-center font-bricolage font-bold text-licht text-base px-7 py-3.5 rounded-full self-start sm:self-auto"
                style={{ background: 'linear-gradient(135deg,#9c4e17,#fe9043)' }}
              >
                Start a project →
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Transition(BrandGuidelinesPage);
